import { useMemo } from "react";
import { useApp } from "./useApp.js";
import { totals as computeTotals } from "../utils/tax.js";

// Compares in cents (not the raw decimals) to avoid float-rounding false
// positives, e.g. 130.00 vs 130.00 coming back as 129.99999999999997.
const exceedsInCents = (line5, line4) => Math.round((line5 || 0) * 100) > Math.round((line4 || 0) * 100);

// Prefers the backend's computed totals (state.returnTotals, set by
// trucksService.js calls from TrucksStep) - the backend is the source of
// truth for all tax math. Falls back to local computation only for the
// unauthenticated dashboard-demo path (SEEDS in data/rates.js), which has
// no backend return to load from - mirrors BusinessStep's existing
// getToken()-gated network pattern.
//
// creditsExceedTax is derived here (not recomputed separately by each step)
// so CreditsStep and ReviewStep can never disagree about whether credits
// (line5) exceed the total tax (line4) - both read this same flag, and it
// updates automatically whenever returnTotals changes (every vehicle or
// credit add/edit/delete round-trips through the backend and dispatches a
// fresh returnTotals - see TrucksStep/CreditsStep).
export function useTotals() {
  const { state } = useApp();
  // Mileage-exceeded amended returns keep their vehicles in the isolated
  // state.mileageVehicles, not state.trucks (Original Return) - see
  // AppContext.jsx's SET_RETURN routing comment.
  const localTrucks =
    state.returnType === "amended" && state.amendReason === "mileage" ? state.mileageVehicles : state.trucks;
  return useMemo(() => {
    const t = state.returnTotals ?? computeTotals(localTrucks, state.month);
    return { ...t, creditsExceedTax: exceedsInCents(t.line5, t.line4) };
  }, [state.returnTotals, localTrucks, state.month]);
}
